<script setup>
import {inject, onMounted, ref, provide, computed} from "vue";
//import functions
import {showMessage} from "@/utils/showMessage.js";
import {persianDate} from "@/utils/persianDate.js";
//import components
import {Dialog} from "primevue";
import UserPermissionsSkeleton from "@/components/users/UserPermissionsSkeleton.vue";
import {Icon} from "@iconify/vue";
//import stores
import {useUserStore} from "@/stores/user.js";
import {useConfigStore} from "@/stores/config.js";
//import composables
import {useBreakpoint} from "@/composables/useBreakpoint";
import {useLoadingFlags} from "@/composables/useLoadingFlags.js";
import {useToast} from "primevue/usetoast";
import {useConfirm} from "primevue/useconfirm";

//stores
const userStore = useUserStore();
const configStore = useConfigStore();

//loading flags
const {
  fetching,
  saving,
  loading,
  setFetching,
  setSaving,
  setLoading,
} = useLoadingFlags()

//composables
const {isXL} = useBreakpoint();
const confirm = useConfirm();
const toast = useToast();

//emits
const emit = defineEmits(['refresh']);

//props
const props = defineProps({
  _selectedUser: Object
});

//injects
const visible = inject('isShowRolesModal')

//component variables
const user = ref({
  id: props._selectedUser?.id ? props._selectedUser.id : '',
  permissions: []
});
const allPermissions = ref([]);
const groupedPermissions = ref([]);
const selectedRoles = ref([])
/*const roles = computed(() => {
  const games = configStore.configs.allGames || []
  return games.flatMap(game => {
    const services = []
    if (game.hasBoostService) services.push({label: `${game.game} - Boost`, value: `${game.game} - Boost`})
    if (game.hasCurrencyService) services.push({label: `${game.game} - Currency`, value: `${game.game} - Currency`})
    if (game.hasAccountService) services.push({label: `${game.game} - Account`, value: `${game.game} - Account`})
    return services
  })
})*/

//global functions

//component functions
const getAllPermissions = async () => {
  await userStore.action_getAllPermissions();
  allPermissions.value = userStore.getAllPermissions;
  groupedPermissions.value = Object.values(
      allPermissions.value.reduce((acc, item) => {
        if (!acc[item.category]) {
          acc[item.category] = {
            categoryTitle: item.category,
            permissions: []
          };
        }
        acc[item.category].permissions.push(item);
        return acc;
      }, {})
  );
}

const get_user = async () => {
  const result = await userStore.action_getCompleteProfile3({id: user.value.id});
  if (result.result) {
    user.value.permissions = userStore.getSelectedUserPermissions;
  }
}

const setPermissions = async () => {
  confirm.require({
    group: 'templating',
    header: 'Confirmation',
    message: {
      title: `You are setting permissions for user "${props._selectedUser.name}"`,
      text: 'Are you sure?'
    },
    icon: 'pi pi-exclamation-circle',
    rejectProps: {
      class: '!border-slate-500 !py-2 !px-4 !text-slate-300',
      label: 'Cancel',
      outlined: true,
      size: 'small'
    },
    acceptProps: {
      class: '!border-blue-500 !py-2 !px-4 !text-blue-500 hover:!bg-blue-500 hover:!text-slate-200',
      outlined: true,
      label: 'Save',
      size: 'small'
    },
    accept: async () => {
      setSaving(true)
      try {
        const res = await userStore.action_setPermissions({
          userID: user.value.id,
          permissions: user.value.permissions
        });
        if (res === true) {
          //toast.add({severity: 'success', summary: 'Successful', detail: 'Permissions edited', life: 500});
          showMessage('Permissions edited', 'success');
          visible.value = false
        } else {
          //toast.add({severity: 'error', summary: 'Failed', detail: res, life: 1500});
          showMessage(res, 'error');
        }
      } catch (err) {
        //toast.add({severity: 'error', summary: 'Failed', detail: err, life: 1500});
        showMessage(err, 'error');
      } finally {
        setSaving(false)
      }
    },
    reject: () => {
      return
    }
  });
};

const fetchInitialData = async () => {
  setFetching(true);
  try {
    // Run both in parallel (faster)
    await Promise.all([get_user()]);
  } catch (error) {
    console.error('Error fetching initial data:', error);
  } finally {
    setFetching(false);
  }
}

//hooks
onMounted(() => {
  fetchInitialData()
});

//provides
</script>
<template>
  <Dialog v-model:visible="visible"
          :class="['max-w-3xl !border-0 !rounded-md !bg-[#0e1726]', isXL ? '' : 'w-full']"
          :position="isXL ? 'center' : 'bottom'"
          closable dismissable-mask draggable modal
          content-class="flex flex-col gap-4 overflow-hidden h-full w-full max-w-[calc(100vw-20px)] max-h-[calc(100vh-100px)] !p-2">
    <template #header>
      <div class="text-base py-3">
        Account Roles
      </div>
    </template>
    <template #closebutton="{closeCallback}">
      <div class="h-full w-[1px] bg-slate-700"></div>
      <button @click="closeCallback" type="button" class="text-white-dark outline-none hover:text-red-500">
        <Icon icon="pajamas:close-xs"/>
      </button>
    </template>
    <div class="flex-1 flex flex-col gap-4 overflow-y-auto custom-scrollbar pt-4 text-xs 2xl:text-sm pr-2">
      <UserPermissionsSkeleton v-if="fetching"/>
      <form v-else class="space-y-5">
        <div class="grid grid-cols-1 sm:grid-cols-2 bg-slate-800 rounded-md text-slate-300 p-4">
          <p>Username: {{ _selectedUser.name }}</p>
          <p>Last Update: {{ _selectedUser.lastUpdate ? persianDate(_selectedUser.lastUpdate) : 'ــــ' }}</p>
        </div>

        <div v-for="game in configStore.configs.allGames"
             class="flex flex-col gap-4 pb-6 border-b border-gray-800 last:border-0">
          <p class="text-base">{{ game.game }}</p>
          <!--          <p v-if="game.hasBoostService">{{ game.game }} - Boost</p>
                    <p v-if="game.hasCurrencyService">{{ game.game }} - Currency</p>
                    <p v-if="game.hasAccountService">{{ game.game }} - Account</p>-->
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div v-if="game.hasBoostService" class="flex flex-row items-center gap-2">
              <div
                  class="flex justify-center items-center rounded-tr-md font-semibold border-[#e0e6ed] dark:border-[#17263c]"
              >
                <input :id="game.game + ' - Boost'" type="checkbox"
                       v-model="selectedRoles"
                       :value="game.game + ' - Boost'"
                       class="form-checkbox rounded-[4px] bg-transparent border-[#e0e6ed] dark:border-white-dark ltr:mr-0 rtl:ml-0 checked:bg-blue-800 size-4 checked:hover:bg-blue-800 cursor-pointer outline-none checked:border-0 focus:outline-none focus:ring-0 focus:ring-offset-0"
                       :checked="selectedRoles.includes(game.game + ' - Boost')"/>
              </div>
              <label :for="game.game + ' - Boost'" class="w-full text-sm select-none cursor-pointer">
                {{ game.game }} - Boost</label>
            </div>
            <div v-if="game.hasCurrencyService" class="flex flex-row items-center gap-2">
              <div
                  class="flex justify-center items-center rounded-tr-md font-semibold border-[#e0e6ed] dark:border-[#17263c]"
              >
                <input :id="game.game + ' - Currency'" type="checkbox"
                       v-model="selectedRoles"
                       :value="game.game + ' - Currency'"
                       class="form-checkbox rounded-[4px] bg-transparent border-[#e0e6ed] dark:border-white-dark ltr:mr-0 rtl:ml-0 checked:bg-blue-800 size-4 checked:hover:bg-blue-800 cursor-pointer outline-none checked:border-0 focus:outline-none focus:ring-0 focus:ring-offset-0"
                       :checked="selectedRoles.includes(game.game + ' - Currency')"/>
              </div>
              <label :for="game.game + ' - Currency'" class="w-full text-sm select-none cursor-pointer">
                {{ game.game }} - Currency</label>
            </div>
            <div v-if="game.hasAccountService" class="flex flex-row items-center gap-2">
              <div
                  class="flex justify-center items-center rounded-tr-md font-semibold border-[#e0e6ed] dark:border-[#17263c]"
              >
                <input :id="game.game + ' - Account'" type="checkbox"
                       v-model="selectedRoles"
                       :value="game.game + ' - Account'"
                       class="form-checkbox rounded-[4px] bg-transparent border-[#e0e6ed] dark:border-white-dark ltr:mr-0 rtl:ml-0 checked:bg-blue-800 size-4 checked:hover:bg-blue-800 cursor-pointer outline-none checked:border-0 focus:outline-none focus:ring-0 focus:ring-offset-0"
                       :checked="selectedRoles.includes(game.game + ' - Account')"/>
              </div>
              <label :for="game.game + ' - Account'" class="w-full text-sm select-none cursor-pointer">
                {{ game.game }} - Account</label>
            </div>
          </div>
        </div>
      </form>
    </div>

    <div class="flex flex-wrap justify-end gap-3">
      <button type="submit" class="btn btn-outline-primary py-1">
        <Icon v-if="saving" icon="eos-icons:bubble-loading" height="24"/>
        Save
      </button>
    </div>
  </Dialog>
</template>
<style>

</style>